import React, { useState } from 'react'
import { sendEmailVerification, signInWithEmailAndPassword } from 'firebase/auth'
import { Link, useNavigate } from 'react-router-dom'
import { FirebaseError } from 'firebase/app'
import { Box, TextField, Typography, Button, Divider } from '@mui/material'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import { useSnackbar } from '../contexts/SnackbarContext'
import { auth } from '../firebase'
import AuthLayout from '../layouts/AuthLayout'

export const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isEmailError, setIsEmailError] = useState(false)
  const [emailErrorText, setEmailErrorText] = useState('')
  const [isPasswordError, setIsPasswordError] = useState(false)
  const [passwordErrorText, setPasswordErrorText] = useState('')
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbar()

  const convertToJapanese = (errorMessage: string) => {
    switch (errorMessage) {
      case 'auth/email-already-in-use':
        return 'このメールアドレスはすでに登録されています'
      case 'auth/invalid-email':
        return 'メールアドレスの形式が正しくありません'
      case 'auth/user-disabled':
        return 'このユーザーは無効化されています'
      case 'auth/user-not-found':
        return 'ユーザーが見つかりません'
      case 'auth/wrong-password':
        return 'パスワードが間違っています'
      case 'auth/operation-not-allowed':
        return 'この操作は許可されていません'
      default:
        return 'メールアドレスまたはパスワードが間違っています'
    }
  }

  const handleLogin = async () => {
    if (email === '') {
      setIsEmailError(true)
      setEmailErrorText('メールアドレスを入力してください')
    } else {
      setIsEmailError(false)
      setEmailErrorText('')
    }

    if (password === '') {
      setIsPasswordError(true)
      setPasswordErrorText('パスワードを入力してください')
    } else {
      setIsPasswordError(false)
      setPasswordErrorText('')
    }

    if (email === '' || password === '') {
      return
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      if (userCredential.user.emailVerified) {
        navigate('/')
      } else {
        await sendEmailVerification(userCredential.user)
        setIsEmailError(true)
        showSnackbar(
          'メールアドレスが未確認です。メールボックスをチェックし、メール内のリンクから確認手続きを完了してください',
          'error'
        )
      }
    } catch (e: unknown) {
      setIsEmailError(true)
      setIsPasswordError(true)
      if (e instanceof FirebaseError) {
        const newErrorMessage = convertToJapanese(e.code)
        showSnackbar(newErrorMessage, 'error')
      } else {
        showSnackbar('不明なエラーが発生しました。', 'error')
      }
    }
  }

  return (
    <AuthLayout title="Chopsticksにログイン">
        <TextField
          id="outlined-basic"
          label="メールアドレス"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={isEmailError}
          helperText={emailErrorText || ' '}
          fullWidth
          sx={{
            margin: '12px auto 0',
          }}
        />

        <TextField
          id="outlined-password-input"
          label="パスワード"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={isPasswordError}
          helperText={passwordErrorText || ' '}
          fullWidth
          sx={{
            margin: '12px auto 0',
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleLogin}
          size="large"
          sx={{
            width: '120px',
            fontWeight: 'bold',
            fontSize: '18px',
            margin: '16px auto',
          }}
        >
          ログイン
        </Button>

        <Typography
          variant="body2"
          sx={{
            mb: '4px',
            mt: '8px',
          }}
        >
          パスワードをお忘れですか？
        </Typography>
        <Button
          color="info"
          variant="text"
          component={Link}
          to="/PasswordReset"
          sx={{
            fontWeight: 'bold',
          }}
        >
          パスワードをリセットする
        </Button>

        <Box sx={{ position: 'relative', padding: 2, my: 6 }}>
          <Divider />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              px: 4,
            }}
          >
            <Typography>または</Typography>
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            mb: '12px',
          }}
        >
          アカウントをお持ちでないですか？
        </Typography>

        <Button
          variant="outlined"
          component={Link}
          to="/signUp"
          size="large"
          startIcon={<PersonAddAlt1Icon />}
          sx={{
            fontSize: '18px',
            fontWeight: 'bold',
          }}
        >
          新しく作成する
        </Button>
    </AuthLayout>
  )
}
